import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Edit,
  Eye,
  Trash2,
  AlertTriangle,
  FileText,
} from 'lucide-react';

interface Document {
  id: number;
  title: string;
  description?: string;
  category?: string;
  nis2_chapter?: string;
  nis2_article?: string;
  nis2_letter?: string;
  status: 'draft' | 'review' | 'approved' | 'archived';
  version: number;
  created_by: number;
  creator_first_name: string;
  creator_last_name: string;
  created_at: string;
  updated_at: string;
  tags?: string[];
  review_date?: string;
  review_period?: string;
  content?: string;
}

interface DocumentsTableProps {
  documents: Document[];
  onViewDocument: (document: Document) => void;
  onEditDocument: (document: Document) => void;
  onDeleteDocument: (documentId: number) => void;
  getItemName: (codelistType: string, code: string) => string;
  visibleColumns: string[];
  sortConfig: {
    key: string | null;
    direction: 'asc' | 'desc';
  };
  onSort: (key: string) => void;
  columnFilters: Record<string, string>;
  onColumnFilter: (column: string, value: string) => void;
  onClearColumnFilter: (column: string) => void;
  // Bulk selection props
  selectedDocumentIds: Set<number>;
  selectAllMode: 'none' | 'page' | 'all';
  totalFilteredDocuments: number;
  onSelectDocument: (documentId: number, selected: boolean) => void;
  onSelectAll: (mode: 'none' | 'page' | 'all') => void;
  // Permission props
  canEditDocument: (document: Document) => boolean;
  canDeleteDocument: (document: Document) => boolean;
}

// Column definitions with display names and sortable/filterable properties
export const COLUMN_DEFINITIONS = {
  title: { label: 'Název', sortable: true, filterable: true },
  description: { label: 'Popis', sortable: true, filterable: true },
  category: { label: 'Kategorie', sortable: true, filterable: true },
  status: { label: 'Status', sortable: true, filterable: true },
  version: { label: 'Verze', sortable: true, filterable: false },
  creator_name: { label: 'Autor', sortable: true, filterable: true },
  nis2_chapter: { label: 'NIS2 Kapitola', sortable: true, filterable: true },
  nis2_article: { label: 'NIS2 Article', sortable: true, filterable: true },
  nis2_letter: { label: 'NIS2 Letter', sortable: true, filterable: true },
  tags: { label: 'Štítky', sortable: false, filterable: true },
  review_date: { label: 'Datum kontroly', sortable: true, filterable: false },
  review_period: { label: 'Interval kontroly', sortable: true, filterable: true },
  created_at: { label: 'Vytvořeno', sortable: true, filterable: false },
  updated_at: { label: 'Upraveno', sortable: true, filterable: false },
  actions: { label: 'Akce', sortable: false, filterable: false },
};

const DocumentsTable: React.FC<DocumentsTableProps> = ({
  documents,
  onViewDocument,
  onEditDocument,
  onDeleteDocument,
  getItemName,
  visibleColumns,
  sortConfig,
  onSort,
  columnFilters,
  onColumnFilter,
  onClearColumnFilter,
  selectedDocumentIds,
  selectAllMode,
  totalFilteredDocuments,
  onSelectDocument,
  onSelectAll,
  canEditDocument,
  canDeleteDocument,
}) => {
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'draft': return 'secondary';
      case 'review': return 'default';
      case 'approved': return 'outline';
      case 'archived': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Návrh';
      case 'review': return 'Na schválení';
      case 'approved': return 'Schváleno';
      case 'archived': return 'Archivováno';
      default: return status;
    }
  };

  const getCategoryLabel = (category: string) => {
    const categoryLabels: { [key: string]: string } = {
      'policy': 'Politiky',
      'procedure': 'Postupy',
      'process': 'Procesy',
      'role': 'Role a odpovědnosti',
      'risk': 'Řízení rizik',
      'incident': 'Incident Response',
      'compliance': 'Compliance',
      'training': 'Školení',
      'audit': 'Audit',
      'technical': 'Technická dokumentace'
    };
    return categoryLabels[category] || category;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('cs-CZ');
  };

  const formatReviewPeriod = (period: string) => {
    const periodOptions: { [key: string]: string } = {
      '1d': '1 den',
      '7d': '1 týden', 
      '14d': '2 týdny',
      '30d': '1 měsíc',
      '3m': '3 měsíce',
      '6m': '6 měsíců',
      '1y': '1 rok',
      '2y': '2 roky',
      '3y': '3 roky',
      '5y': '5 let'
    };
    return periodOptions[period] || period;
  };

  // Determine checkbox states
  const currentPageDocumentIds = documents.filter(doc => doc.id).map(doc => doc.id!);
  
  const handleMasterCheckboxChange = (checked: boolean) => {
    if (checked) {
      // Always select all filtered documents (not just current page)
      onSelectAll('all');
    } else {
      // Unselect all
      onSelectAll('none');
    }
  };

  const getMasterCheckboxState = (): boolean | 'indeterminate' => {
    if (selectAllMode === 'all') {
      return true;
    } else if (selectedDocumentIds.size > 0) {
      // If any documents are selected, show indeterminate state
      return 'indeterminate';
    } else {
      return false;
    }
  };

  const renderCellContent = (document: Document, column: string) => {
    switch (column) {
      case 'title':
        return (
          <div className="flex items-center space-x-2">
            <FileText className="h-4 w-4" />
            <span className="font-medium">{document.title}</span>
          </div>
        );
      case 'description':
        return <span className="max-w-xs truncate" title={document.description}>{document.description || '-'}</span>;
      case 'category':
        return document.category ? (
          <Badge variant="secondary">{getCategoryLabel(document.category)}</Badge>
        ) : '-';
      case 'status':
        return <Badge variant={getStatusBadgeVariant(document.status)}>{getStatusLabel(document.status)}</Badge>;
      case 'version':
        return <Badge variant="outline">v{document.version}</Badge>;
      case 'creator_name':
        return document.creator_first_name && document.creator_last_name 
          ? `${document.creator_first_name} ${document.creator_last_name}` 
          : '-';
      case 'nis2_chapter':
        return document.nis2_article && document.nis2_letter 
          ? `${getItemName('nis2_articles', document.nis2_article)} - ${getItemName('nis2_letters', document.nis2_letter)}`
          : '-';
      case 'nis2_article':
        return document.nis2_article ? getItemName('nis2_articles', document.nis2_article) : '-';
      case 'nis2_letter':
        return document.nis2_letter ? getItemName('nis2_letters', document.nis2_letter) : '-';
      case 'tags':
        return document.tags && document.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {document.tags.slice(0, 2).map((tag, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {document.tags.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{document.tags.length - 2}
              </Badge>
            )}
          </div>
        ) : '-';
      case 'review_date':
        return document.review_date ? (
          <div className="flex items-center space-x-1">
            <span>{formatDate(document.review_date)}</span>
            {new Date(document.review_date) < new Date() && (
              <AlertTriangle className="h-3 w-3 text-amber-600" />
            )}
          </div>
        ) : '-';
      case 'review_period':
        return document.review_period ? (
          <Badge variant="outline">{formatReviewPeriod(document.review_period)}</Badge>
        ) : '-';
      case 'created_at':
        return document.created_at ? formatDate(document.created_at) : '-';
      case 'updated_at':
        return document.updated_at ? formatDate(document.updated_at) : '-';
      case 'actions':
        return (
          <div className="flex space-x-1">
            <Button variant="outline" size="sm" onClick={() => onViewDocument(document)}>
              <Eye className="h-3 w-3" />
            </Button>
            {canEditDocument(document) && (
              <Button variant="outline" size="sm" onClick={() => onEditDocument(document)}>
                <Edit className="h-3 w-3" />
              </Button>
            )}
            {canDeleteDocument(document) && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onDeleteDocument(document.id)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        );
      default:
        return '-';
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {/* Checkbox column header */}
          <TableHead className="w-12">
            <Checkbox
              checked={getMasterCheckboxState()}
              onCheckedChange={(checked: boolean) => handleMasterCheckboxChange(checked)}
              aria-label={
                selectAllMode === 'all' 
                  ? `Zrušit výběr všech ${totalFilteredDocuments} dokumentů`
                  : `Vybrat všech ${totalFilteredDocuments} vyfiltrovaných dokumentů`
              }
            />
          </TableHead>
          {visibleColumns.map((column) => {
            const columnDef = COLUMN_DEFINITIONS[column as keyof typeof COLUMN_DEFINITIONS];
            if (!columnDef) return null;

            return (
              <TableHead
                key={column}
                sortable={columnDef.sortable}
                sorted={sortConfig.key === column ? sortConfig.direction : null}
                onSort={() => columnDef.sortable && onSort(column)}
                filterable={columnDef.filterable}
                filterValue={columnFilters[column] || ''}
                onFilterChange={(value) => columnDef.filterable && onColumnFilter(column, value)}
                onClearFilter={() => columnDef.filterable && onClearColumnFilter(column)}
                columnName={column}
              >
                {columnDef.label}
              </TableHead>
            );
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.map((document) => (
          <TableRow key={document.id}>
            {/* Checkbox column cell */}
            <TableCell>
              {document.id && (
                <Checkbox
                  checked={selectedDocumentIds.has(document.id)}
                  onCheckedChange={(checked: boolean) => onSelectDocument(document.id!, checked)}
                  aria-label={`Vybrat dokument ${document.title}`}
                />
              )}
            </TableCell>
            {visibleColumns.map((column) => (
              <TableCell key={column}>
                {renderCellContent(document, column)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default DocumentsTable;
